/**
 * Money (spec §10).
 *
 * Amounts are ALWAYS integer minor units (piastres/cents/fils) plus an ISO
 * currency code. Binary floating point is never used for money anywhere in the
 * system — not in the database, not in the API, not in the desktop cache.
 */

export interface Money {
  /** Integer minor units. May be negative (credits, reversals). */
  readonly minor: number;
  readonly currency: string;
}

export interface CurrencySpec {
  readonly code: string;
  /** Number of minor units per major unit, as an exponent: 2 => 1.00 */
  readonly exponent: number;
  /** Smallest amount that can actually be transacted, in minor units. */
  readonly cashRoundingMinor: number;
}

/**
 * Only currencies configured for a clinic may be used. Exponents follow
 * ISO 4217. Add new entries deliberately — a wrong exponent silently
 * misprices everything in that currency.
 */
export const CURRENCIES: Record<string, CurrencySpec> = {
  USD: { code: 'USD', exponent: 2, cashRoundingMinor: 1 },
  EUR: { code: 'EUR', exponent: 2, cashRoundingMinor: 1 },
  SAR: { code: 'SAR', exponent: 2, cashRoundingMinor: 5 },
  AED: { code: 'AED', exponent: 2, cashRoundingMinor: 25 },
  SYP: { code: 'SYP', exponent: 2, cashRoundingMinor: 10000 },
  TRY: { code: 'TRY', exponent: 2, cashRoundingMinor: 1 },
  JOD: { code: 'JOD', exponent: 3, cashRoundingMinor: 5 },
  KWD: { code: 'KWD', exponent: 3, cashRoundingMinor: 5 },
};

export class CurrencyMismatchError extends Error {
  constructor(a: string, b: string) {
    super(`Refusing to combine unlike currencies ${a} and ${b}; an explicit FX rate and date are required.`);
    this.name = 'CurrencyMismatchError';
  }
}

export function money(minor: number, currency: string): Money {
  if (!Number.isSafeInteger(minor)) {
    throw new RangeError(`Money must be an integer number of minor units, got ${minor}`);
  }
  const spec = CURRENCIES[currency];
  if (!spec) throw new RangeError(`Unknown currency ${currency}`);
  return { minor, currency };
}

export function zero(currency: string): Money {
  return money(0, currency);
}

function assertSame(a: Money, b: Money): void {
  if (a.currency !== b.currency) throw new CurrencyMismatchError(a.currency, b.currency);
}

export function add(a: Money, b: Money): Money {
  assertSame(a, b);
  return money(a.minor + b.minor, a.currency);
}

export function subtract(a: Money, b: Money): Money {
  assertSame(a, b);
  return money(a.minor - b.minor, a.currency);
}

export function sum(items: readonly Money[], currency: string): Money {
  return items.reduce<Money>((acc, m) => add(acc, m), zero(currency));
}

export function negate(a: Money): Money {
  return money(-a.minor, a.currency);
}

export function isZero(a: Money): boolean {
  return a.minor === 0;
}

export function compare(a: Money, b: Money): number {
  assertSame(a, b);
  return a.minor === b.minor ? 0 : a.minor < b.minor ? -1 : 1;
}

export type RoundingMode = 'half_up' | 'half_even' | 'down' | 'up';

/**
 * Multiply a money amount by a rational factor (e.g. a 7.5% tax or a 40%
 * commission) with an explicit rounding mode. The factor is expressed as a
 * numerator/denominator pair so no decimal literal ever enters the calculation.
 */
export function multiplyRational(
  a: Money,
  numerator: number,
  denominator: number,
  mode: RoundingMode = 'half_up',
): Money {
  if (!Number.isInteger(numerator) || !Number.isInteger(denominator) || denominator === 0) {
    throw new RangeError('multiplyRational requires integer numerator and non-zero integer denominator');
  }
  return money(roundDiv(a.minor * numerator, denominator, mode), a.currency);
}

/** Percentage helper: 7.5% => percentBasisPoints = 750. */
export function percentage(a: Money, basisPoints: number, mode: RoundingMode = 'half_up'): Money {
  return multiplyRational(a, basisPoints, 10_000, mode);
}

export function roundDiv(numerator: number, denominator: number, mode: RoundingMode): number {
  if (denominator < 0) {
    numerator = -numerator;
    denominator = -denominator;
  }
  const q = Math.trunc(numerator / denominator);
  const r = numerator - q * denominator;
  if (r === 0) return q;
  const sign = numerator < 0 ? -1 : 1;
  const twice = Math.abs(r) * 2;
  switch (mode) {
    case 'down':
      return q;
    case 'up':
      return q + sign;
    case 'half_up':
      return twice >= denominator ? q + sign : q;
    case 'half_even': {
      if (twice > denominator) return q + sign;
      if (twice < denominator) return q;
      return q % 2 === 0 ? q : q + sign;
    }
  }
}

/**
 * Split an amount into n parts whose sum is EXACTLY the original (largest
 * remainder). Used for installment schedules — the classic "1000 / 3" bug where
 * three 333.33 installments silently lose a piastre.
 */
export function allocateEvenly(total: Money, parts: number): Money[] {
  if (!Number.isInteger(parts) || parts <= 0) throw new RangeError('parts must be a positive integer');
  const base = Math.trunc(total.minor / parts);
  let remainder = total.minor - base * parts;
  const step = remainder >= 0 ? 1 : -1;
  const out: Money[] = [];
  for (let i = 0; i < parts; i++) {
    let v = base;
    if (remainder !== 0) {
      v += step;
      remainder -= step;
    }
    out.push(money(v, total.currency));
  }
  return out;
}

/**
 * Allocate an amount across weights (e.g. splitting a payment over invoice
 * lines, or a discount over items) such that the parts sum exactly to `total`.
 */
export function allocateByWeights(total: Money, weights: readonly number[]): Money[] {
  const totalWeight = weights.reduce((a, b) => a + b, 0);
  if (totalWeight === 0) return allocateEvenly(total, weights.length);
  const raw = weights.map((w) => (total.minor * w) / totalWeight);
  const floors = raw.map((r) => Math.floor(r));
  let remainder = total.minor - floors.reduce((a, b) => a + b, 0);
  const order = raw
    .map((r, i) => ({ i, frac: r - Math.floor(r) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);
  const out = floors.slice();
  for (const { i } of order) {
    if (remainder === 0) break;
    out[i] = out[i]! + 1;
    remainder -= 1;
  }
  return out.map((m) => money(m, total.currency));
}

/** Format for display. Never used for storage or arithmetic. */
export function formatMoney(a: Money, locale: string): string {
  const spec = CURRENCIES[a.currency];
  if (!spec) throw new RangeError(`Unknown currency ${a.currency}`);
  const major = a.minor / 10 ** spec.exponent;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: a.currency,
    minimumFractionDigits: spec.exponent,
    maximumFractionDigits: spec.exponent,
  }).format(major);
}

/** Parse user input ("1 234,50") into minor units for the given currency. */
export function parseMoney(input: string, currency: string): Money {
  const spec = CURRENCIES[currency];
  if (!spec) throw new RangeError(`Unknown currency ${currency}`);
  const normalised = input
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660)) // Arabic-Indic digits
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0)) // Extended Arabic-Indic
    .replace(/[\s  ']/g, '')
    .replace(/[.,](?=\d{3}\b)/g, '')
    .replace(',', '.');
  if (!/^-?\d+(\.\d+)?$/.test(normalised)) throw new RangeError(`Cannot parse "${input}" as an amount`);
  const neg = normalised.startsWith('-');
  const [intPart = '0', fracPart = ''] = normalised.replace('-', '').split('.');
  const frac = (fracPart + '0'.repeat(spec.exponent)).slice(0, spec.exponent);
  const minor = Number(intPart) * 10 ** spec.exponent + Number(frac || '0');
  return money(neg ? -minor : minor, currency);
}
